import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, firstValueFrom, timeout } from 'rxjs';
import { IDraftListing, PRIMARY_PROPERTY_TYPE_META, isAddressStepValid } from './draft-listing.types';
import {
  IPrivateListing,
  amenityDetailsFrom,
  RV_TYPES,
  landTypesForCategory,
  type Category,
  type IAddOn,
} from './mock-listings.data';
import { AuthService } from '../auth/auth.service';
import { ToastService } from '../toast/toast.service';
import { HostListingMetaService } from '../host/host-listing-meta.service';
import { HostListingsService } from '../host/host-listings.service';
import { AnalyticsService } from '../analytics/analytics.service';
import { ListingApiService } from '../backend-api/listing-api.service';
import { SiteApiService } from '../backend-api/site-api.service';
import { GrowClientsService } from '../grow/grow-clients.service';
import type { IListing } from '@curbnturf/entities';
import { HelperFunctions } from '@curbnturf/entities';
import {
  draftToRealListingBooking,
  draftToRealListingDescription,
  draftToRealListingExtras,
  draftToRealListingLocation,
  draftToRealListingPolicies,
  draftToRealPotentialHazards,
  draftToRealPropertyTypes,
  draftToRealSiteDetails,
  draftToRealSiteIsolation,
  draftToRealSiteName,
  draftToRealSiteVehicles,
  omitUnchangedBooking,
  realListingToDraft,
} from './draft-listing-real-mapper';

import {
  IPublishedSnapshot,
  readPublishedSnapshot as readSnapshot,
  writePublishedSnapshot,
  deletePublishedSnapshot,
  clearAllPublishedSnapshots,
} from './published-snapshot.util';
import {
  readActiveDraft,
  readParkedDrafts,
  readShelvedDrafts,
  writeActiveDraft,
  writeParkedDrafts,
  writeShelvedDrafts,
} from './draft-store.util';
import { measureDataUrl } from '../photos/measure.util';
// Re-export so existing consumers of '@cnt-workspace/data-access' keep working.
export { IPublishedSnapshot, readPublishedSnapshot } from './published-snapshot.util';

/**
 * Persists the in-progress listing for the host onboarding wizard at `/hosting/new`
 * and, on publish, creates the real backend listing (see `createRealListing`) —
 * `/listing?id=N` resolves it straight from the server (`ListingFacade`/`ListingApiService.fetch`),
 * and `/search` picks it up once the separate search index catches up.
 *
 * Only one in-progress draft per user. Resuming the wizard reads from
 * `localStorage[cnt-listing-draft]`.
 */
@Injectable({ providedIn: 'root' })
export class HostListingDraftService {
  private platformId = inject(PLATFORM_ID);
  private auth = inject(AuthService);
  private toasts = inject(ToastService);
  private meta = inject(HostListingMetaService);
  private hostListings = inject(HostListingsService);
  private analytics = inject(AnalyticsService);
  private listingSvc = inject(ListingApiService);
  private siteSvc = inject(SiteApiService);
  private growClients = inject(GrowClientsService);

  /** R7/A — Tracks the last (phase, step) tuple we fired `wizard_step_completed` for,
   *  so backwards navigation and repeat edits don't double-count. */
  private lastFiredStep: string | null = null;

  private readonly _draft$ = new BehaviorSubject<IDraftListing | null>(null);
  readonly draft$: Observable<IDraftListing | null> = this._draft$.asObservable();

  /** Non-null while the wizard is editing an already-published listing. */
  private _editingListingId: number | null = null;
  /** Stash of the user's in-progress new-listing draft while in edit mode. */
  private _savedNewListingDraft: IDraftListing | null = null;
  /** The signed-in account's email: whose drafts get parked on sign-out. */
  private ownerEmail: string | null = null;
  /** LIFO stack of new-listing drafts shelved when the host duplicates a
   *  listing. Surfaced on the dashboard's resume-draft card. */
  private readonly _shelvedDrafts$ = new BehaviorSubject<IDraftListing[]>([]);
  readonly shelvedDrafts$: Observable<IDraftListing[]> = this._shelvedDrafts$.asObservable();

  get editingListingId(): number | null {
    return this._editingListingId;
  }
  get isEditing(): boolean {
    return this._editingListingId !== null;
  }
  get shelvedDrafts(): IDraftListing[] {
    return this._shelvedDrafts$.value;
  }
  /** Synchronous accessor for the in-flight draft — handy for callers that
   *  need to read once (e.g., compute a copy title) without subscribing. */
  get activeDraft(): IDraftListing | null {
    return this._draft$.value;
  }

  constructor() {
    this._draft$.next(readActiveDraft());
    this._shelvedDrafts$.next(readShelvedDrafts());
    if (isPlatformBrowser(this.platformId)) {
      this.auth.currentUser$.subscribe((user) => {
        const email = user?.email || null;
        if (email && email !== this.ownerEmail) this.restoreParkedDrafts(email);
        this.ownerEmail = email;
      });
      this.auth.loggedOut$.subscribe(() => {
        this.parkDrafts();
        this._editingListingId = null;
        this._savedNewListingDraft = null;
        this._draft$.next(null);
        this._shelvedDrafts$.next([]);
        void writeActiveDraft(null);
        void writeShelvedDrafts([]);
        void clearAllPublishedSnapshots();
      });
    }
  }

  /** On sign-out, put the account's unpublished drafts away under its email
   *  instead of deleting them: the next person on this device doesn't see
   *  them, and they come back when the same account signs in. Reads the
   *  new-listing draft from its edit-mode stash when an edit is open. */
  private parkDrafts(): void {
    if (!this.ownerEmail) return;
    const active = this._editingListingId !== null ? this._savedNewListingDraft : this._draft$.value;
    const drafts = [...this._shelvedDrafts$.value, ...(active ? [active] : [])].filter(
      (d) => this.draftHasContent(d) && !d.publishedAt,
    );
    if (!drafts.length) return;
    void writeParkedDrafts(this.ownerEmail, [...readParkedDrafts(this.ownerEmail), ...drafts]);
  }

  /** On sign-in, give the account back the drafts `parkDrafts` put away. The
   *  newest becomes the active draft unless one is already in progress (a
   *  draft started signed-out, before "sign in to publish"), which keeps its
   *  place; everything else goes on the shelf. */
  private restoreParkedDrafts(email: string): void {
    const parked = readParkedDrafts(email);
    if (!parked.length) return;
    const shelf = [...this._shelvedDrafts$.value];
    if (this._draft$.value || this._editingListingId !== null) {
      shelf.push(...parked);
    } else {
      const newest = parked[parked.length - 1];
      shelf.push(...parked.slice(0, -1));
      this._draft$.next(newest);
      void writeActiveDraft(newest);
    }
    this.writeShelved(shelf);
    void writeParkedDrafts(email, []);
  }

  /** Current draft snapshot (synchronous). */
  get current(): IDraftListing | null {
    return this._draft$.value;
  }

  /**
   * Per-step completion for the active draft. Drives the resume-card progress UI
   * shown on /hosting and /properties. Mirrors the phase-hub logic in the
   * wizard. Returns null when no draft exists.
   *
   * The "review" step is intentionally counted as the last actionable step but
   * is considered complete only when the publish gate would pass.
   */
  get completion(): {
    stepsDone: number;
    stepsTotal: number;
    pct: number;
    phasesDone: [boolean, boolean, boolean];
  } | null {
    return this.completionFor(this._draft$.value);
  }

  /** Per-step completion for an arbitrary draft — used by the shelved-card
   *  renderer so each saved copy shows its own progress without touching
   *  the in-flight slot. */
  completionFor(d: IDraftListing | null): {
    stepsDone: number;
    stepsTotal: number;
    pct: number;
    phasesDone: [boolean, boolean, boolean];
  } | null {
    if (!d) return null;
    const [phase1, phase2, phase3] = this.stepValidities(d);
    const allSteps = [...phase1, ...phase2, ...phase3];
    const stepsDone = allSteps.filter(Boolean).length;
    const stepsTotal = allSteps.length;
    return {
      stepsDone,
      stepsTotal,
      pct: Math.round((stepsDone / stepsTotal) * 100),
      phasesDone: [phase1.every(Boolean), phase2.every(Boolean), phase3.every(Boolean)],
    };
  }

  /**
   * True when the active draft satisfies the requirements of the given (phase, step)
   * tuple. Drives the wizard's Next-button gate so users can't advance past a step
   * that hasn't met its minimum.
   */
  isStepValid(phase: 1 | 2 | 3, step: number): boolean {
    const d = this._draft$.value;
    if (!d) return false;
    const [p1, p2, p3] = this.stepValidities(d);
    const phaseSteps = phase === 1 ? p1 : phase === 2 ? p2 : p3;
    return phaseSteps[step] ?? false;
  }

  /** Human-readable hint shown when a step's Next is disabled. */
  stepValidationHint(phase: 1 | 2 | 3, step: number): string {
    const hints: Record<string, string> = {
      '1-0': 'Pick a primary property type to continue.',
      '1-1': "Add city, state, pin the map — and if you're not the owner, the landowner's details.",
      '1-2': 'Set the max guest count to continue.',
      '1-3': 'Pick at least one amenity (standard or custom) to continue.',
      '1-4': 'Pick at least one rig type, or switch to tents-only.',
      '2-0': 'Upload at least 3 photos to continue.',
      '2-1': 'Title needs 8+ chars and description needs 150+.',
      '2-2': 'Pick visibility, noise, and road conditions to continue.',
      '2-3': 'Add your name and phone number to continue.',
      '3-0': 'Set check-in / check-out times and min nights.',
      '3-1': 'Save your house rules to continue.',
      '3-2': 'Set a nightly price and a cancellation policy to continue.',
      '3-3': 'A few required fields are still missing.',
    };
    return hints[`${phase}-${step}`] ?? '';
  }

  /**
   * Per-phase step-validity arrays. Single source of truth so `completion`,
   * `isStepValid`, and `missingRequiredFields` all stay in sync.
   */
  private stepValidities(d: IDraftListing): [boolean[], boolean[], boolean[]] {
    const phase1 = [
      !!d.primaryType && (d.primaryType !== 'custom' || (d.customPrimaryLabel?.trim().length ?? 0) >= 2),
      isAddressStepValid(d),
      typeof d.guestCapacity === 'number' && d.guestCapacity > 0,
      (d.amenities?.length ?? 0) + (d.customAmenities?.length ?? 0) > 0,
      d.tentMode === 'tents-only' || (d.vehicleTypes?.length ?? 0) > 0,
    ];
    const phase2 = [
      // Photos are advisory, not gating — a host can proceed with <3 and finish
      // within the 30-day grace period (see missingRequiredFields()'s doc comment).
      true,
      !!d.title && d.title.length >= 8 && !!d.description && d.description.length >= 150,
      !!d.visibility?.length && !!d.noiseLevel && !!d.roadConditions?.length,
      this.isHostProfileComplete(d),
    ];
    const phase3 = [
      !!d.checkInTime && !!d.checkOutTime && typeof d.minNights === 'number',
      !!d.rules,
      // Any positive price completes the step (F7). There is no viable-price
      // floor to enforce: the server adds its fee on top of what the host lists,
      // so a host who lists $10 is paid $10 and the guest is charged $20. The
      // $10 minimum this once enforced was guarding a case that cannot occur.
      typeof d.nightlyPrice === 'number' && d.nightlyPrice > 0 && !!d.cancellationTier,
      this.missingRequiredFields(d).length === 0,
    ];
    return [phase1, phase2, phase3];
  }

  /**
   * Merge a partial update into the current draft (or create a fresh one if
   * none exists). Bumps `updatedAt`, persists, and emits.
   */
  saveDraft(patch: Partial<IDraftListing>): IDraftListing {
    const now = new Date().toISOString();
    const existing = this._draft$.value;
    const next: IDraftListing = existing
      ? { ...existing, ...patch, updatedAt: now }
      : {
          id: this.newId(),
          createdAt: now,
          updatedAt: now,
          currentPhase: 1,
          currentStep: 0,
          // Default to every rig type accepted so a host who never touches
          // the vehicles step can still continue past its gate.
          vehicleTypes: RV_TYPES.map((o) => o.id),
          // Default booking window so a host who never touches the bookings
          // step can still continue past its gate.
          checkInTime: '14:00:00',
          checkOutTime: '12:00:00',
          minNights: 1,
          maxNights: 14,
          minNoticeHours: 24,
          maxNoticeWeeks: 12,
          ...patch,
        };
    if (this._editingListingId !== null) {
      this.persistEdit(this._editingListingId, next);
    } else {
      this.write(next);
    }
    this._draft$.next(next);
    this.maybeFireStepCompleted(existing, next);
    return next;
  }

  /** R7/A — Fire `wizard_step_completed` only when (phase, step) strictly advances
   *  from the previous draft state. Suppresses events on step-content edits, backward
   *  navigation, and edit-mode. */
  private maybeFireStepCompleted(prev: IDraftListing | null, next: IDraftListing): void {
    if (this._editingListingId !== null) return;
    const key = `${next.currentPhase}:${next.currentStep}`;
    if (!prev) {
      // First save on a fresh draft — record but don't fire (wizard_entered already covers arrival).
      this.lastFiredStep = key;
      return;
    }
    const advanced =
      next.currentPhase > prev.currentPhase ||
      (next.currentPhase === prev.currentPhase && next.currentStep > prev.currentStep);
    if (!advanced) return;
    if (this.lastFiredStep === key) return;
    this.lastFiredStep = key;
    this.analytics.track('wizard_step_completed', {
      sourcePersona: next.sourcePersona ?? null,
      phase: next.currentPhase,
      step: next.currentStep,
    });
  }

  /**
   * Begin editing an already-published listing from this device's snapshot.
   * The wizard reads from this draft and `saveDraft` writes back to the
   * listing's snapshot in place instead of the new-listing slot. Returns null
   * when no snapshot exists. Only the offline fallback for
   * `loadForEditFromServer`: the snapshot can be older than the listing, and
   * saving an old copy overwrites the newer one.
   */
  loadForEdit(listingId: number): IDraftListing | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    const snap = readSnapshot(listingId);
    if (!snap) return null;
    // Stash whatever draft was in flight so we can restore on exit.
    this._savedNewListingDraft = this._draft$.value;
    this._editingListingId = listingId;
    this._draft$.next(snap.draft);
    return snap.draft;
  }

  /**
   * How the editor opens an existing listing. Live-fetches the real listing
   * (+ its first site) and reconstructs an editable draft via
   * `realListingToDraft` (best-effort — see that function's doc comment for
   * what can't round-trip), on top of this device's snapshot if it has one.
   * Always the server first: the snapshot is only as new as the last edit made
   * in this browser, so a change made elsewhere (admin, another device) was
   * shown stale and then overwritten by the next save. Returns null when the
   * fetch fails or the listing isn't found; the caller then tries `loadForEdit`.
   */
  async loadForEditFromServer(listingId: number): Promise<IDraftListing | null> {
    const draft = await this.snapshotFromServer(listingId);
    if (!draft) return null;
    this._savedNewListingDraft = this._draft$.value;
    this._editingListingId = listingId;
    this._draft$.next(draft);
    return draft;
  }

  /**
   * Make sure a listing has a local snapshot to copy from, fetching it from
   * the server when it doesn't (F28). Copying used to work only for a listing
   * published from this same browser — anywhere else "Copy as new" said
   * "Could not copy this property." Resolves false only when the listing
   * can't be fetched. Doesn't enter edit mode.
   */
  async ensureDraftSource(listingId: number): Promise<boolean> {
    if (this.resolveDraftSource(listingId)) return true;
    return !!(await this.snapshotFromServer(listingId));
  }

  /** Live-fetch a listing, rebuild an editable draft from it (`realListingToDraft`,
   *  best-effort), and save it as this browser's snapshot for the listing.
   *  Null on a 404/access denial or off the browser. */
  private async snapshotFromServer(listingId: number): Promise<IDraftListing | null> {
    if (!isPlatformBrowser(this.platformId)) return null;
    let listing: IListing;
    try {
      listing = await firstValueFrom(this.listingSvc.fetch(listingId));
    } catch {
      return null;
    }
    if (!listing?.id) return null;

    // The server wins on every field it stores. The local snapshot, when there is
    // one, still carries what it doesn't (draft id, duplication and sales-handoff
    // bookkeeping, persona attribution), so it goes underneath rather than away.
    const now = new Date().toISOString();
    const draft: IDraftListing = {
      id: this.newId(),
      createdAt: now,
      currentPhase: 1,
      currentStep: 0,
      ...readSnapshot(listingId)?.draft,
      updatedAt: now,
      publishedListingId: listing.id,
      ...realListingToDraft(listing),
    };

    this.savePublishedSnapshot(listing.id, {
      draft,
      hostName: this.hostNameFromUser(listing.user),
      hostInitials: this.hostInitialsFromUser(listing.user),
      hostAvatar: listing.user?.photo
        ? HelperFunctions.getPhotoUrlSquareSmall(listing.user.photo)
        : 'assets/images/host_opportunity.webp',
      hostJoinedYear: new Date().getFullYear(),
      hostEmail: listing.user?.email ?? '',
      realSiteId: listing.sites?.[0]?.id,
    });

    return draft;
  }

  /**
   * Leave edit mode and restore the prior new-listing draft (if any). Called
   * when the wizard component unloads.
   */
  exitEdit(): void {
    if (this._editingListingId === null) return;
    this._editingListingId = null;
    this._draft$.next(this._savedNewListingDraft);
    this._savedNewListingDraft = null;
  }

  /**
   * Clone an existing listing's snapshot into a brand-new unpublished draft.
   * Used by the "Duplicate" action on /hosting cards so a host can spin up a
   * second site on the same property without re-walking the wizard from
   * scratch.
   *
   * - If currently in edit mode, exits first so the duplicate becomes the new
   *   in-flight new-listing draft (not an edit-mode write).
   * - If a meaningful new-listing draft is already in flight, it's pushed onto
   *   the shelved-drafts stack so the host doesn't lose work. The dashboard
   *   surfaces a "Resume shelved draft" hint while the stack is non-empty.
   * - Source resolves from the publish-time snapshot first (covers user-
   *   published + previously-edited listings); falls back to the seed
   *   `IPrivateListing` for unsnapshotted mock catalog entries.
   * - Every add-on gets a fresh id so future edits in the new draft don't
   *   bleed back to the source (same isolation pattern as the bulk builder).
   *
   * Returns the new draft, or null if no source could be resolved.
   */
  duplicateAsDraft(sourceListingId: number, customTitle?: string): IDraftListing | null {
    if (this._editingListingId !== null) this.exitEdit();

    const source = this.resolveDraftSource(sourceListingId);
    if (!source) return null;

    // Shelve the current in-flight draft if it has meaningful content so
    // the host doesn't lose it. "Meaningful" = anything beyond the bare
    // skeleton fields.
    const current = this._draft$.value;
    if (current && this.draftHasContent(current)) {
      this.writeShelved([...this._shelvedDrafts$.value, current]);
    }

    const now = new Date().toISOString();
    // Structured clone, then strip identity + status + regenerate fresh ids
    // anywhere ids are local to the draft (add-ons today; future per-site
    // ids would go here too).
    const clone: IDraftListing = JSON.parse(JSON.stringify(source));
    clone.id = this.newId();
    clone.createdAt = now;
    clone.updatedAt = now;
    clone.currentPhase = 1;
    clone.currentStep = 0;
    clone.title = customTitle?.trim() || this.suggestCopyTitle(clone.title);
    clone.clonedFromListingId = sourceListingId;
    delete clone.publishedAt;
    delete clone.publishedListingId;
    if (Array.isArray(clone.addOns)) {
      clone.addOns = clone.addOns.map((a) => ({
        ...a,
        id: `addon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      }));
    }

    this._draft$.next(clone);
    this.write(clone);
    return clone;
  }

  /**
   * Clone an existing listing and publish it immediately in one shot —
   * powers the "Or publish as-is" link on the listing-card rename modal.
   *
   * Wraps the clone in stash/restore so the host's in-flight new-listing
   * draft isn't bulldozed by `publish()`'s draft-wiping side effects.
   * Returns the new listing, or null when the source can't be resolved or
   * the clone would fail validation.
   */
  async duplicateAndPublish(sourceListingId: number, customTitle?: string): Promise<IPrivateListing | null> {
    if (this._editingListingId !== null) this.exitEdit();
    const source = this.resolveDraftSource(sourceListingId);
    if (!source) return null;

    const now = new Date().toISOString();
    const clone: IDraftListing = JSON.parse(JSON.stringify(source));
    clone.id = this.newId();
    clone.createdAt = now;
    clone.updatedAt = now;
    clone.currentPhase = 1;
    clone.currentStep = 0;
    clone.title = customTitle?.trim() || this.suggestCopyTitle(clone.title);
    clone.clonedFromListingId = sourceListingId;
    delete clone.publishedAt;
    delete clone.publishedListingId;
    if (Array.isArray(clone.addOns)) {
      clone.addOns = clone.addOns.map((a) => ({
        ...a,
        id: `addon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      }));
    }

    // Defensive: bail before mutating draft state if the clone wouldn't pass
    // publish-validation. Falls back gracefully via the caller.
    if (this.missingRequiredFields(clone).length > 0) return null;

    // Stash any in-flight draft so publish()'s wipe doesn't lose work.
    const stashed = this._draft$.value;
    this._draft$.next(clone);
    this.write(clone);

    let listing: IPrivateListing | null = null;
    try {
      listing = await this.publish();
    } catch {
      listing = null;
    } finally {
      // publish() clears localStorage's draft key on success; restore both
      // the in-memory and persisted state from the stash either way.
      if (stashed) {
        this._draft$.next(stashed);
        this.write(stashed);
      } else {
        this._draft$.next(null);
      }
    }
    return listing;
  }

  /**
   * Like `duplicateAsDraft()` but writes the clone straight to the shelved-
   * drafts stack instead of swapping the in-flight draft. Powers the
   * "Save to drafts" branch on the listing-card rename modal — the host
   * stays where they are (typically /hosting) and the copy waits on the
   * shelf for later. Returns the new draft, or null when no source resolves.
   */
  duplicateAsShelvedDraft(sourceListingId: number, customTitle?: string): IDraftListing | null {
    const source = this.resolveDraftSource(sourceListingId);
    if (!source) return null;

    const now = new Date().toISOString();
    const clone: IDraftListing = JSON.parse(JSON.stringify(source));
    clone.id = this.newId();
    clone.createdAt = now;
    clone.updatedAt = now;
    clone.currentPhase = 1;
    clone.currentStep = 0;
    clone.title = customTitle?.trim() || this.suggestCopyTitle(clone.title);
    clone.clonedFromListingId = sourceListingId;
    delete clone.publishedAt;
    delete clone.publishedListingId;
    if (Array.isArray(clone.addOns)) {
      clone.addOns = clone.addOns.map((a) => ({
        ...a,
        id: `addon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      }));
    }

    this.writeShelved([...this._shelvedDrafts$.value, clone]);
    return clone;
  }

  /**
   * Fork the in-flight new-listing draft — clone its current state with a
   * fresh id and shelve the clone so the host keeps editing the original
   * while the copy waits to be picked up later (via `resumeShelvedDraft()`).
   *
   * Returns the clone, or null when there's no draft or the draft is the
   * bare skeleton (no fields to be worth forking).
   */
  forkCurrentDraft(customTitle?: string): IDraftListing | null {
    const current = this._draft$.value;
    if (!current || !this.draftHasContent(current)) return null;

    const now = new Date().toISOString();
    const clone: IDraftListing = JSON.parse(JSON.stringify(current));
    clone.id = this.newId();
    clone.createdAt = now;
    clone.updatedAt = now;
    clone.title = customTitle?.trim() || this.suggestCopyTitle(clone.title);
    delete clone.publishedAt;
    delete clone.publishedListingId;
    if (Array.isArray(clone.addOns)) {
      clone.addOns = clone.addOns.map((a) => ({
        ...a,
        id: `addon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      }));
    }

    this.writeShelved([...this._shelvedDrafts$.value, clone]);
    return clone;
  }

  /**
   * Mint N shelved copies of a listing in one pass — drives the inline
   * "Add another site" N-stepper on the dashboard. Each iteration reuses
   * the existing `duplicateAsShelvedDraft()` path; because
   * `suggestCopyTitle()` walks the shelved-drafts list to find unused
   * titles, the pattern-aware naming chains naturally (Pad 3, Pad 4,
   * Pad 5, …) without any explicit numbering.
   *
   * Returns the array of newly-shelved drafts, or [] if none were
   * created (bad source id or count ≤ 0).
   */
  duplicateAsShelvedDraftBatch(sourceListingId: number, count: number): IDraftListing[] {
    const n = Math.max(0, Math.min(20, Math.floor(count)));
    const drafts: IDraftListing[] = [];
    for (let i = 0; i < n; i++) {
      const d = this.duplicateAsShelvedDraft(sourceListingId);
      if (d) drafts.push(d);
    }
    return drafts;
  }

  /** Move a shelved draft from one index to another. Mutates the LIFO order
   *  the dashboard renders. Persists immediately via writeShelved(). */
  reorderShelvedDraft(fromIndex: number, toIndex: number): void {
    const stack = this._shelvedDrafts$.value.slice();
    if (fromIndex < 0 || fromIndex >= stack.length) return;
    const clampedTo = Math.max(0, Math.min(stack.length - 1, toIndex));
    if (fromIndex === clampedTo) return;
    const [moved] = stack.splice(fromIndex, 1);
    stack.splice(clampedTo, 0, moved);
    this.writeShelved(stack);
  }

  /** Patch a shelved draft's title in place — drives the inline title
   *  rename on the shelved card. Bumps `updatedAt` and persists. No-op if
   *  the draft id isn't on the shelf or the new title is empty. */
  renameShelvedDraft(draftId: string, newTitle: string): void {
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    const stack = this._shelvedDrafts$.value.slice();
    const idx = stack.findIndex((d) => d.id === draftId);
    if (idx === -1) return;
    stack[idx] = { ...stack[idx], title: trimmed, updatedAt: new Date().toISOString() };
    this.writeShelved(stack);
  }

  /** Swap the in-flight new-listing draft with the most recently shelved one.
   *  The current in-flight draft (if meaningful) takes its slot on the stack. */
  resumeShelvedDraft(): IDraftListing | null {
    const stack = this._shelvedDrafts$.value;
    if (stack.length === 0) return null;
    return this.resumeShelvedDraftById(stack[stack.length - 1].id);
  }

  /** Swap a specific shelved draft (by its draft id) with the in-flight one —
   *  drives per-draft Resume buttons on the dashboard so the host can pick
   *  any draft, not just the top of the stack. */
  resumeShelvedDraftById(draftId: string): IDraftListing | null {
    const stack = this._shelvedDrafts$.value;
    const idx = stack.findIndex((d) => d.id === draftId);
    if (idx === -1) return null;
    const next = stack[idx];
    const rest = stack.filter((d) => d.id !== draftId);
    const current = this._draft$.value;
    const newStack = current && this.draftHasContent(current) ? [...rest, current] : rest;
    this.writeShelved(newStack);
    this._draft$.next(next);
    this.write(next);
    return next;
  }

  /** Discard the most recently shelved draft (host explicitly drops it). */
  discardShelvedDraft(): void {
    const stack = this._shelvedDrafts$.value;
    if (stack.length === 0) return;
    this.writeShelved(stack.slice(0, -1));
  }

  /** Discard a specific shelved draft (by its draft id). */
  discardShelvedDraftById(draftId: string): void {
    this.writeShelved(this._shelvedDrafts$.value.filter((d) => d.id !== draftId));
  }

  /** Walk an owned listing's `clonedFromListingId` chain via publish-time
   *  snapshots until the chain terminates. Returns the *root* listing id of
   *  the property — single-listing properties return their own id. Bounded
   *  at 8 hops as a safety net against corrupted data forming a cycle. */
  lineageRootOf(listingId: number): number {
    let cursor = listingId;
    for (let i = 0; i < 8; i++) {
      const snap = readSnapshot(cursor);
      const parent = snap?.draft?.clonedFromListingId;
      if (parent == null || parent === cursor) return cursor;
      cursor = parent;
    }
    return cursor;
  }

  /** Group a host's owned listings by their property root so multi-site
   *  parcels (vineyard with three pads, brewery with two) render as a
   *  cohesive section on the dashboard. Single-site groups still appear —
   *  the caller decides whether to render them as sections or flat cards. */
  groupOwnedByProperty(listings: IPrivateListing[]): {
    rootId: number;
    rootTitle: string;
    sites: IPrivateListing[];
  }[] {
    const byRoot = new Map<number, IPrivateListing[]>();
    for (const l of listings) {
      const root = this.lineageRootOf(l.id);
      const arr = byRoot.get(root) ?? [];
      arr.push(l);
      byRoot.set(root, arr);
    }
    const result: { rootId: number; rootTitle: string; sites: IPrivateListing[] }[] = [];
    for (const [rootId, sites] of byRoot.entries()) {
      // Title-sort within a group so D1's pattern-aware names (Pad 1, Pad 2,
      // Pad 3) line up in numeric order.
      const ordered = [...sites].sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true }));
      const rootListing = ordered.find((l) => l.id === rootId) ?? ordered[0];
      result.push({ rootId, rootTitle: rootListing.title, sites: ordered });
    }
    // Groups by most-recent member id (newest property to the top).
    return result.sort((a, b) => Math.max(...b.sites.map((l) => l.id)) - Math.max(...a.sites.map((l) => l.id)));
  }

  /** Smart copy-title suggestion. Hosts almost never want a literal "(copy)"
   *  — they want the next pad number / site letter. We detect common
   *  patterns in the source title and increment them:
   *
   *  - "Heritage Oak — Pad 1"  → "Heritage Oak — Pad 2"
   *  - "Site 12"               → "Site 13"
   *  - "Foo Site A"            → "Foo Site B" (single uppercase letter)
   *
   *  Falls back to the legacy "(copy)" / "(copy 2)" suffix when no pattern
   *  matches. In every case we walk known titles (this host's own published
   *  listings, via `HostListingsService.list()`, plus in-flight + shelved
   *  drafts) and keep incrementing until we mint an unused one, so the
   *  staircase never returns.
   */
  suggestCopyTitle(baseTitle: string | undefined): string {
    const raw = (baseTitle ?? '').trim();
    if (!raw) return '(copy)';

    const taken = new Set<string>();
    for (const l of this.hostListings.list()) if (l.title) taken.add(l.title);
    const current = this._draft$.value;
    if (current?.title) taken.add(current.title);
    for (const d of this._shelvedDrafts$.value) if (d.title) taken.add(d.title);

    // 1) Trailing integer — strongest signal. "Pad 3" → "Pad 4".
    const numMatch = raw.match(/^(.*?)(\d+)\s*$/);
    if (numMatch) {
      const prefix = numMatch[1];
      let n = parseInt(numMatch[2], 10) + 1;
      // Bounded loop — find the next unused integer.
      for (let i = 0; i < 200; i++, n++) {
        const candidate = `${prefix}${n}`;
        if (!taken.has(candidate)) return candidate;
      }
    }

    // 2) Trailing single uppercase letter — "Site A" → "Site B".
    //    Stop at Z so we don't roll into "AA"; defer to the copy suffix.
    const letterMatch = raw.match(/^(.*?\s)([A-Y])\s*$/);
    if (letterMatch) {
      const prefix = letterMatch[1];
      let code = letterMatch[2].charCodeAt(0) + 1;
      for (let i = 0; i < 26 && code <= 'Z'.charCodeAt(0); i++, code++) {
        const candidate = `${prefix}${String.fromCharCode(code)}`;
        if (!taken.has(candidate)) return candidate;
      }
    }

    // 3) Fallback — strip an existing "(copy N?)" suffix from the base,
    //    then mint the smallest unused ordinal.
    const base = raw.replace(/\s*\(copy(?:\s*\d+)?\)\s*$/i, '');
    const first = `${base} (copy)`;
    if (!taken.has(first)) return first;
    for (let n = 2; n < 100; n++) {
      const candidate = `${base} (copy ${n})`;
      if (!taken.has(candidate)) return candidate;
    }
    return `${base} (copy ${Date.now()})`;
  }

  /** Resolve a source draft for duplication — the publish-time snapshot, always
   *  present for a listing this host published from this browser. Returns null
   *  when there's no local snapshot to duplicate from. */
  private resolveDraftSource(listingId: number): IDraftListing | null {
    return readSnapshot(listingId)?.draft ?? null;
  }

  /** A draft is "meaningful" once the host has filled in any user-visible
   *  field beyond the bare skeleton — drives the auto-shelve gate. */
  private draftHasContent(d: IDraftListing): boolean {
    return !!(d.primaryType || d.title || d.address || (d.photos?.length ?? 0) > 0 || d.description);
  }

  private writeShelved(stack: IDraftListing[]): void {
    this._shelvedDrafts$.next(stack);
    if (!isPlatformBrowser(this.platformId)) return;
    writeShelvedDrafts(stack).catch((err) => {
      console.error('[draft] shelved-drafts write failed', err);
      this.toasts.error('Could not save your shelved draft — storage may be full.');
    });
  }

  /**
   * Final "Save changes" step in edit mode — re-runs persistence against the
   * current draft and returns the refreshed listing. No-op when not editing.
   */
  async saveEdit(): Promise<IPrivateListing | null> {
    if (this._editingListingId === null) return null;
    const draft = this._draft$.value;
    if (!draft) return null;
    const listingId = this._editingListingId;

    // Real backend push only happens at this explicit checkpoint — NOT on
    // every autosave tick (see persistEdit's own doc comment for why that
    // distinction matters).
    const snap = readSnapshot(listingId);
    let toPersist = draft;
    if (snap?.realSiteId) {
      const user = this.auth.currentUser;
      if (user?.id) {
        // F21: read the live listing first. A full re-send otherwise overwrites
        // what the draft cannot know (the site's paused state) or what the
        // server will not store as sent (Instant Book without the owner's
        // permission). A failed read aborts the save rather than writing blind.
        const live = await firstValueFrom(this.listingSvc.fetch(listingId));
        await this.applyDraftToRealListing(listingId, snap.realSiteId, draft, live);
        toPersist = {
          ...draft,
          addOns: await this.syncRealExtras(listingId, draft),
          ...(await this.savedPhotos(listingId)),
        };
        this._draft$.next(toPersist);
      }
    }
    return this.persistEdit(listingId, toPersist);
  }

  /** The listing's photos as the server now has them, in the draft's shape.
   *  `syncDraftPhotos` uploads only `data:` entries, so a draft that kept its
   *  `data:` URLs after a save had the next save delete those photos from the
   *  server and upload them all again. Empty (the draft keeps its own) if the
   *  re-read fails; the save itself has already gone through. */
  private async savedPhotos(listingId: number): Promise<Pick<IDraftListing, 'photos' | 'photoCaptions' | 'photoMeta'>> {
    try {
      const { photos, photoCaptions, photoMeta } = realListingToDraft(
        await firstValueFrom(this.listingSvc.fetch(listingId)),
      );
      return { photos, photoCaptions, photoMeta };
    } catch (err) {
      console.error('[draft] could not re-read photos after saving', err);
      return {};
    }
  }

  /**
   * Public checkpoint for the standalone add-ons editors (which don't go
   * through the full wizard's `saveEdit()`) — pushes the given listing's
   * *currently-saved* snapshot add-ons to the real backend, if it's a real
   * listing. No-op for mock-only listings. Called once per explicit
   * "Done"/"Save" action, not per row edit (see syncRealExtras's own comment
   * on why the full-replace endpoint pairs by array position).
   */
  async syncAddOnsIfReal(listingId: number): Promise<void> {
    const snap = readSnapshot(listingId);
    if (!snap?.realSiteId) return;
    try {
      const addOns = await this.syncRealExtras(listingId, snap.draft);
      const draft = { ...snap.draft, addOns };
      this.persistEdit(listingId, draft);
      if (this._editingListingId === listingId) this._draft$.next(draft);
    } catch (err) {
      console.error('[draft] real extras sync failed', err);
      this.toasts.error('Add-ons saved locally, but syncing to the server failed.');
    }
  }

  /**
   * Push `draft.addOns` to the real backend (`PUT /listing/:id/extras/`) and
   * return them with the server-assigned `IListingExtra.id` merged in as
   * `realExtraId`, so a later guest booking can reference the real extra
   * (see reservation-real-mapper.ts's `buildReservationCreatePayload`).
   *
   * The endpoint is full-replace and echoes the saved extras back in the
   * same order they were sent — there's no other unique key to match on
   * (extras have no client-supplied identifier), so pairing by array index
   * is the same positional contract curbnturf's own host wizard relies on.
   */
  private async syncRealExtras(listingId: number, draft: IDraftListing): Promise<IAddOn[]> {
    const addOns = draft.addOns ?? [];
    const updated = await firstValueFrom(this.listingSvc.updateExtras(listingId, draftToRealListingExtras(draft)));
    const realExtras = updated.extras ?? [];
    return addOns.map((a, i) => ({ ...a, realExtraId: realExtras[i]?.id }));
  }

  /**
   * Write the snapshot for an edited listing so `/listing?id=N` reflects the new
   * state on next read (via the snapshot branch of `getListingDetail()`). Local-only
   * and cheap — this is also called from `saveDraft()`'s autosave path (fires on
   * every wizard field change while editing), so it must NOT make real backend
   * calls itself; those only happen once, at the explicit `saveEdit()` checkpoint
   * above.
   */
  private persistEdit(listingId: number, draft: IDraftListing): IPrivateListing {
    const snap = readSnapshot(listingId);
    if (snap) {
      this.savePublishedSnapshot(listingId, { ...snap, draft });
    }
    return this.draftToListing(draft, listingId);
  }

  /** Wipe any in-progress draft. */
  discardDraft(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this._draft$.next(null);
    writeActiveDraft(null).catch((err) => console.error('[draft] discard failed', err));
  }

  /**
   * Create the real backend listing + site for a draft, in the sequence the
   * server actually requires and curbnturf's own sign-up-state effects prove
   * out: a BARE create (`{ user }` only — matches the server's own geocoding
   * step expectations, see draft-listing-real-mapper.ts's doc comment) → real
   * id assigned → location/booking/description/hazards set one at a time via
   * their own dedicated endpoints → add one empty site (real site id
   * assigned) → fill in site details/isolation/price/name → upload photos.
   *
   * Returns the real server-assigned listing id, which becomes THE id used
   * everywhere — /search, /listing?id=N, and the host dashboard all resolve
   * off this one real identity instead of a separately-tracked local one.
   *
   * Throws on any step's failure — a partially-created real listing is left
   * in place rather than silently falling back to mock-only, since silently
   * swallowing backend errors would defeat the point of wiring this for real.
   */
  /**
   * Create the listing for this draft, or reuse the one a previous publish
   * attempt already created.
   *
   * `publish()` creates before it validates: the rows exist by the time the
   * server refuses to enable them, and nothing rolls them back. Reusing them on
   * the retry is what keeps the damage at one listing per draft instead of one
   * per attempt. The retry re-pushes the whole draft, so the correction the host
   * just made — the isolation level the server complained about — actually
   * reaches the server.
   *
   * A stale id must never dead-end the draft. If reuse fails for any reason (the
   * listing was deleted server-side, the draft moved between accounts), the ids
   * are dropped and a fresh listing is created: one extra orphan is a far better
   * outcome than a draft that can no longer be published at all.
   */
  private async createOrReuseRealListing(
    draft: IDraftListing,
    userId: number,
  ): Promise<{ listingId: number; siteId: number }> {
    const { pendingListingId, pendingSiteId } = draft;
    if (typeof pendingListingId === 'number' && typeof pendingSiteId === 'number') {
      try {
        await this.applyDraftToRealListing(pendingListingId, pendingSiteId, draft);
        return { listingId: pendingListingId, siteId: pendingSiteId };
      } catch (err) {
        console.error('[draft] could not reuse the listing from the last publish attempt', err);
        this.saveDraft({ pendingListingId: undefined, pendingSiteId: undefined });
      }
    }
    return this.createRealListing(draft, userId);
  }

  private async createRealListing(
    draft: IDraftListing,
    userId: number,
  ): Promise<{ listingId: number; siteId: number }> {
    const created = await firstValueFrom(this.listingSvc.create({ user: { id: userId } }));
    if (!created.id) throw new Error('Server did not return a listing id.');
    const listingId = created.id;

    const withSite = await firstValueFrom(this.listingSvc.addNewSites(listingId, 1));
    const site = withSite.sites?.[0];
    if (!site?.id) throw new Error('Server did not return a site id.');

    await this.applyDraftToRealListing(listingId, site.id, draft);
    return { listingId, siteId: site.id };
  }

  /**
   * Push a whole draft onto an already-real listing + site: the six listing-level
   * endpoints, then the site's own content via `fillSiteContent`.
   *
   * Three callers, which is why it is extracted rather than inlined — the first
   * two were duplicates of each other before this existed:
   *   - `createRealListing()`, once the bare listing and its site have real ids
   *   - `saveEdit()`, the explicit save checkpoint in edit mode
   *   - `publish()`'s retry path, reconfiguring rows a rejected attempt left behind
   *
   * Every call is an idempotent update against ids that already exist, so running
   * it twice over the same listing is the normal case, not a hazard.
   */
  private async applyDraftToRealListing(
    listingId: number,
    siteId: number,
    draft: IDraftListing,
    live?: IListing,
  ): Promise<void> {
    const booking = draftToRealListingBooking(draft);
    await firstValueFrom(this.listingSvc.updateLocation(listingId, draftToRealListingLocation(draft)));
    await firstValueFrom(
      this.listingSvc.updateBooking(listingId, live ? omitUnchangedBooking(booking, live) : booking),
    );
    await firstValueFrom(this.listingSvc.updateDescription(listingId, draftToRealListingDescription(draft)));
    await firstValueFrom(this.listingSvc.updatePolicies(listingId, draftToRealListingPolicies(draft)));
    const hazards = draftToRealPotentialHazards(draft);
    await firstValueFrom(
      this.listingSvc.updatePotentialHazards(listingId, hazards.potentialHazards, hazards.potentialHazardDescription),
    );
    const propertyTypes = draftToRealPropertyTypes(draft);
    if (propertyTypes) {
      // customPrimaryLabel is passed here deliberately. saveEdit() always sent it
      // and createRealListing() never did, so publishing a listing with a custom
      // property type dropped the host's own label and editing it afterwards put
      // it back. Unifying on the version that sends it fixes that.
      await firstValueFrom(
        this.listingSvc.updatePrimaryTypeAndDescriptors(
          listingId,
          propertyTypes.primaryType,
          propertyTypes.descriptors,
          propertyTypes.customPrimaryLabel,
        ),
      );
    }
    // The site goes live with the listing: publish() enables its first site,
    // with the server's checks, when none is available yet.
    await this.fillSiteContent(siteId, listingId, draft, live);
  }

  /** Push a draft's site-level content (name, amenities/rig caps, vehicles,
   *  isolation/privacy, price) to an already-real site id, and its photos to the
   *  parent listing. Shared by both the initial publish and edit-mode saves.
   *  The listing's own name/description/hazards live at the listing level (see
   *  createRealListing/persistEdit) — the site name below is a separate field.
   *
   *  F15: this used to send details, isolation and price only. The site's name
   *  and its vehicle types were never transmitted, though `canEnable()` requires
   *  a name and `testVehicles()` requires tentOnly or at least one vehicle class.
   *  Publishes got through on server defaults, so the listing went live
   *  describing vehicles the host had not chosen — and the wizard will not let
   *  phase 1 step 5 complete without that very answer.
   *
   *  The name is only sent while the site has none: an edit-mode save (`live`
   *  set) must not rename a site the host or the first publish already named. */
  private async fillSiteContent(
    siteId: number,
    listingId: number,
    draft: IDraftListing,
    live?: IListing,
  ): Promise<void> {
    const liveSite = live?.sites?.find((s) => s.id === siteId);
    if (!liveSite?.name?.trim()) {
      await firstValueFrom(this.siteSvc.updateName(siteId, draftToRealSiteName(draft)));
    }
    await firstValueFrom(this.siteSvc.updateDetails(siteId, draftToRealSiteDetails(draft)));
    await firstValueFrom(this.siteSvc.updateVehicles(siteId, draftToRealSiteVehicles(draft)));
    await firstValueFrom(this.siteSvc.updateIsolation(siteId, draftToRealSiteIsolation(draft)));
    if (typeof draft.nightlyPrice === 'number') {
      await firstValueFrom(this.siteSvc.updatePrice(siteId, { price: draft.nightlyPrice }));
    }
    await this.syncDraftPhotos(listingId, draft.photos ?? [], draft.photoMeta ?? [], draft.photoCaptions ?? []);
  }

  /** Reconciles the wizard's local photo draft with the real `Listing.photos`
   *  (photos here are a Listing-level concept — a listing's wizard always
   *  provisions exactly one Site, but photos are guest-facing gallery content
   *  that belongs to the listing itself, not that one site; see
   *  `realListingToDraft` for the matching read-side change).
   *
   *  Two passes:
   *  1. Reconcile removals — `Phase2PhotosComponent.remove()` only mutates
   *     local draft state, so a photo the host removed in the wizard is
   *     otherwise never actually deleted server-side. Compare the current
   *     server photos against the draft's already-persisted entries (real
   *     `https://` URLs — freshly-picked photos are still `data:` URLs at
   *     this point) and remove whatever's no longer present. Iterate server
   *     photos in reverse index order so each removal doesn't invalidate the
   *     indices of entries not yet checked.
   *  2. Upload new photos — convert the wizard's downscaled JPEG `data:` URLs
   *     into real multipart uploads against `/listing/:id/upload-image`, one
   *     at a time (server accepts a single `files` field per call). Only
   *     `data:` entries are new; anything already an `https://` URL round-
   *     tripped from the server via `realListingToDraft` and must NOT be
   *     re-uploaded, or every `saveEdit()` would duplicate the whole gallery.
   *
   *  Both passes are best-effort per photo — a failure surfaces a toast but
   *  doesn't abort the rest of the publish, since the listing/site already
   *  exist and are worth keeping even if a photo fails.
   *
   *  R15/A — reads per-photo crop rect + rotation from `photoMeta[i]` (added
   *  by the PhotoEditor flow) so the backend can render a true 1:1 crop while
   *  keeping the full 1200px source. Falls back to the zero-crop identity if
   *  metadata is missing (legacy drafts pre-editor).
   *
   *  `photoMeta[i].crop` is normalized 0..1 (relative to the post-rotation
   *  image bounds — rotation is already baked into `photos[i]`'s pixels by
   *  PhotoEditorComponent, so `rotation` here is always sent as identity).
   *  The backend's crop renderer (`createCropEdit` in helpers/src/helpers/
   *  photo.ts, the same one every other photo type in the app already uses)
   *  expects `cropLeft/Top/Right/Bottom` as integer PIXELS, not fractions —
   *  converting here rather than changing that shared renderer.
   *
   *  Known limitation: recropping an *existing* photo in the wizard
   *  (`Phase2PhotosComponent.edit()`) replaces that slot's value with a fresh
   *  `data:` URL, which this function can't distinguish from "removed the old
   *  photo, added a new one" — functionally correct (no duplicates, no
   *  orphaned server photos) but the recropped photo ends up appended at the
   *  end of the gallery rather than keeping its original position. */
  private async syncDraftPhotos(
    listingId: number,
    photos: string[],
    photoMeta: import('./draft-listing.types').IPhotoMeta[],
    captions: string[],
  ): Promise<void> {
    try {
      const listing = await firstValueFrom(this.listingSvc.fetch(listingId));
      const serverPhotos = listing.photos ?? [];
      const keptUrls = new Set(photos.filter((p) => !p.startsWith('data:')));
      for (let i = serverPhotos.length - 1; i >= 0; i--) {
        const url = HelperFunctions.getPhotoUrlOriginal(serverPhotos[i]);
        const key = serverPhotos[i].key;
        if (key && !keptUrls.has(url)) {
          await firstValueFrom(this.listingSvc.removeImageByKey(listingId, key));
        }
      }
    } catch (err) {
      console.error('[draft] photo reconciliation failed', err);
    }

    for (let i = 0; i < photos.length; i++) {
      if (!photos[i].startsWith('data:')) continue;
      try {
        const blob = await (await fetch(photos[i])).blob();
        const meta = photoMeta[i] ?? { crop: { left: 0, top: 0, right: 1, bottom: 1 }, rotation: 0 };
        const { width, height } = await measureDataUrl(photos[i]);
        const form = new FormData();
        form.append('files', blob, `photo-${i}.jpg`);
        form.append('caption', captions[i] ?? '');
        form.append('cropLeft', String(Math.round(meta.crop.left * width)));
        form.append('cropTop', String(Math.round(meta.crop.top * height)));
        form.append('cropRight', String(Math.round(meta.crop.right * width)));
        form.append('cropBottom', String(Math.round(meta.crop.bottom * height)));
        form.append('zoom', '1');
        form.append('rotation', '0');
        await firstValueFrom(this.listingSvc.uploadImage(listingId, form).pipe(timeout(45_000)));
      } catch (err) {
        console.error(`[draft] photo ${i} upload failed`, err);
        this.toasts.error(`Photo ${i + 1} failed to upload — the rest of your listing was saved.`);
      }
    }
  }

  /**
   * Validate required fields and, if satisfied, create the real backend
   * listing (see `createRealListing`) and return it as an `IPrivateListing`
   * using that real id — `/listing?id=N` resolves the same id straight from
   * the backend, and `/search` picks it up once the search index catches up.
   *
   * Returns the new listing on success, throws an Error otherwise (including
   * real backend failures — see `createRealListing`'s doc comment).
   */
  async publish(): Promise<IPrivateListing> {
    const draft = this._draft$.value;
    if (!draft) throw new Error('No draft to publish');
    // Already published and the draft survived (see the post-enable try/catch
    // below — a failure there used to leave the draft in place). Re-publishing
    // would create a SECOND live listing for the same property, which is exactly
    // what a host does when the first attempt appears to have done nothing.
    if (draft.publishedListingId) {
      const already = this.draftToListing(draft, draft.publishedListingId);
      this.discardDraft();
      return already;
    }
    const missing = this.missingRequiredFields(draft);
    if (missing.length > 0) {
      throw new Error(`Cannot publish — missing: ${missing.join(', ')}`);
    }
    const user = this.auth.currentUser;
    if (!user?.id) throw new Error('You must be signed in with a real account to publish.');
    await this.syncHostProfileToAccount(draft);

    const { listingId, siteId } = await this.createOrReuseRealListing(draft, user.id);
    // Remember what exists BEFORE asking the server to enable it. A rejection
    // below throws, and without this the rows created just now would be
    // unreachable — the next click would create another set and leave these
    // behind, which is how three publish attempts produced three orphaned
    // listings (user-test F14).
    this.saveDraft({ pendingListingId: listingId, pendingSiteId: siteId });
    // createRealListing leaves the listing `available: false` server-side —
    // flip it live via POST /listing/:id/enable before treating this as a
    // successful publish (canEnable() failures come back here as `message`
    // rather than a thrown error).
    const enableResult = await this.hostListings.publish(listingId);
    if (!enableResult.success) {
      throw new Error(enableResult.message || 'Your listing was created but could not be published.');
    }
    // PAST THIS LINE THE LISTING IS LIVE. Everything below is local bookkeeping,
    // and none of it may report failure or leave the draft behind: a throw here
    // used to surface as "Publish failed" while the property was already
    // published and bookable, with the draft still sitting at 92% — so the host
    // clicked publish again and got a second live listing for the same property.
    // Observed in testing: two clicks, two listings, no confirmation either time.
    // The aborted in-flight requests that trigger it are a separate bug; this
    // guarantees they can't cost a host a duplicate listing either way.
    const listing = this.draftToListing(draft, listingId);
    try {
      // Persist a snapshot — used by getListingDetail()'s snapshot branch on pages
      // that read a listing's full detail without the real entity in hand (e.g.
      // booking-review/confirm, search cards) so those keep showing the host's real
      // data instead of falling back to the generic stub.
      this.savePublishedSnapshot(listing.id, {
        draft,
        hostName: this.hostNameFromUser(user),
        hostInitials: this.hostInitialsFromUser(user),
        hostAvatar: user?.photoUrl || 'assets/images/host_opportunity.webp',
        hostJoinedYear: new Date().getFullYear(),
        hostEmail: user?.email ?? '',
        realSiteId: siteId,
      });
      const next: IDraftListing = {
        ...draft,
        publishedAt: new Date().toISOString(),
        publishedListingId: listing.id,
        // The rows are live now, so they are no longer "pending". discardDraft()
        // below wipes this object anyway, but it is also the shape handed to
        // analytics and to savePublishedSnapshot, and a published draft that
        // still claims a pending listing would be a confusing thing to read back.
        pendingListingId: undefined,
        pendingSiteId: undefined,
      };
      // R7/A — Fire funnel-conversion event before wiping the draft so we can
      // attribute the publish to whichever persona referred the visitor.
      this.analytics.track('wizard_listing_published', {
        sourcePersona: next.sourcePersona ?? null,
        publishedListingId: next.publishedListingId ?? null,
      });
    } catch (err) {
      // Bookkeeping only — the property is published either way.
      console.error('[draft] post-publish bookkeeping failed', err);
    }
    // Clear the in-progress slot so the user can start a fresh draft next time —
    // the wizard's "create another" flow starts from scratch. Must NOT re-emit
    // `next` here: this service is a root singleton and "Add another listing"
    // is a client-side navigation, so a stale non-null draft would survive into
    // the next wizard mount and get merged into as if it were still in-flight.
    // Outside the try: the draft must go even if the bookkeeping threw, or the
    // wizard keeps offering to publish an already-published property.
    this.discardDraft();
    return listing;
  }

  /**
   * Seeds a fresh draft tagged for a sales rep to build on a referred
   * client's behalf (see `publishForClient`). Any existing active draft not
   * already for this same client is shelved first — reusing the same
   * shelved-drafts stack `duplicateAsShelvedDraft` pushes onto — so a rep's
   * own in-progress listing, or a different client's, is never silently lost.
   */
  startDraftForClient(clientUserId: number, clientName: string, clientEmail?: string): void {
    const existing = this._draft$.value;
    if (existing?.salesForUserId === clientUserId && !existing.publishedAt) {
      // Re-entering the flow for the same client with an unfinished draft — keep progress.
      this.saveDraft({ salesForUserName: clientName, salesForUserEmail: clientEmail });
      return;
    }
    if (existing && this.draftHasContent(existing) && !existing.publishedAt) {
      this.writeShelved([...this._shelvedDrafts$.value, existing]);
    }
    this.discardDraft();
    this.saveDraft({ salesForUserId: clientUserId, salesForUserName: clientName, salesForUserEmail: clientEmail });
  }

  /**
   * Sales-flow counterpart to `publish()` — creates the real backend
   * listing/site under the CLIENT's user id (not the signed-in rep's), and
   * hands off to the client instead of going live:
   *   - never calls `hostListings.publish()`/`enable()`, so an unreviewed sales
   *     draft stays unpublished (`available: false`) and doesn't show up in /search
   *   - skips `hostListings.refreshNow()` (not the rep's own listings)
   *   - still saves a published snapshot, under the client's identity, so
   *     `/properties/:id/edit` can find it later (see editOwnerGuard)
   *   - emails the client: a welcome/login email if they haven't received one
   *     yet (best-effort — a 400 just means it was already sent), then the
   *     "your listing is ready to review" notification
   */
  async publishForClient(): Promise<{ listingId: number }> {
    const draft = this._draft$.value;
    if (!draft) throw new Error('No draft to publish');
    if (!draft.salesForUserId) throw new Error('This draft is not tagged for a client.');
    const missing = this.missingRequiredFields(draft);
    if (missing.length > 0) {
      throw new Error(`Cannot create listing — missing: ${missing.join(', ')}`);
    }

    const clientId = draft.salesForUserId;
    const { listingId, siteId } = await this.createRealListing(draft, clientId);
    const listing = this.draftToListing(draft, listingId);
    this.savePublishedSnapshot(listing.id, {
      draft,
      hostName: draft.salesForUserName || 'New host',
      hostInitials: this.initialsFromName(draft.salesForUserName),
      hostAvatar: 'assets/images/host_opportunity.webp',
      hostJoinedYear: new Date().getFullYear(),
      hostEmail: draft.salesForUserEmail ?? '',
      realSiteId: siteId,
    });
    this.analytics.track('sales_listing_created_for_client', {
      clientUserId: clientId,
      listingId: listing.id,
    });
    // Same draft-leak hazard as publish() — see its comment for why this must
    // not re-emit a stale draft into the singleton's live state.
    this.discardDraft();

    try {
      await firstValueFrom(this.growClients.sendWelcomeEmail(clientId));
    } catch {
      // Already sent (400) or best-effort failure — the listing still exists either way.
    }
    await firstValueFrom(this.growClients.notifyClientListingReady(listing.id));

    return { listingId: listing.id };
  }

  /**
   * Hands an in-progress, possibly-incomplete draft off to the client mid-wizard
   * — an alternative to the rep filling out every required field before the
   * client can pick it up. Unlike `publishForClient()`, there's no
   * `missingRequiredFields` gate; `createRealListing` already tolerates a
   * sparse draft (only `{ user: { id } }` is required server-side, everything
   * else is filled in best-effort — see `ListingApiService.create`).
   *
   * The first call creates the real (unpublished, `available: false`) listing
   * under the client's id and remembers it via `draft.handoffListingId`. The
   * rep keeps working on the same local draft afterward — this does NOT
   * discard it. A repeat call for the same draft just resends the
   * notification against that same listing; it deliberately does not push
   * newer local edits, since by then the client may already be editing the
   * real listing themselves (see editOwnerGuard/loadForEditFromServer) and
   * overwriting their changes with the rep's local copy would be data-lossy.
   */
  async sendHandoffToClient(): Promise<{ listingId: number }> {
    const draft = this._draft$.value;
    if (!draft) throw new Error('No draft to hand off');
    if (!draft.salesForUserId) throw new Error('This draft is not tagged for a client.');

    const clientId = draft.salesForUserId;
    let listingId = draft.handoffListingId;

    if (!listingId) {
      const created = await this.createRealListing(draft, clientId);
      listingId = created.listingId;
      this.savePublishedSnapshot(listingId, {
        draft,
        hostName: draft.salesForUserName || 'New host',
        hostInitials: this.initialsFromName(draft.salesForUserName),
        hostAvatar: 'assets/images/host_opportunity.webp',
        hostJoinedYear: new Date().getFullYear(),
        hostEmail: draft.salesForUserEmail ?? '',
        realSiteId: created.siteId,
      });
      this.analytics.track('sales_listing_handed_off_to_client', { clientUserId: clientId, listingId });
      this.saveDraft({ handoffListingId: listingId });
    }

    try {
      await firstValueFrom(this.growClients.sendWelcomeEmail(clientId));
    } catch {
      // Already sent (400) or best-effort failure — the handoff still proceeds.
    }
    await firstValueFrom(this.growClients.notifyClientListingHandoff(listingId));

    return { listingId };
  }

  /** "Jane Doe" -> "JD"; falls back to "NH" (New Host) for an empty/missing name. */
  private initialsFromName(name: string | undefined): string {
    const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
    const initials = parts
      .map((p) => p.charAt(0).toUpperCase())
      .join('')
      .slice(0, 2);
    return initials || 'NH';
  }

  /**
   * Permanently remove a user-published listing. Coordinates the following
   * so the listing disappears everywhere consistently:
   *   - the real backend record (best-effort delete)
   *   - cnt-published-snapshots in IDB (affects /listing?id=N detail hydration)
   *   - HostListingsService's real ownership cache (affects /properties + /search membership)
   *   - cnt-host-listing-meta (paused/archived flags)
   *
   * Caller is responsible for confirming with the host first — this is irreversible.
   */
  async deletePublishedListing(listingId: number): Promise<void> {
    // Real listings (published post-Phase-2) carry a snapshot with a real
    // site id — delete the backend record too. Best-effort: a failure here
    // still lets the local removal proceed, since the host's own view of
    // "this listing is gone" shouldn't hinge on backend reachability.
    const snap = readSnapshot(listingId);
    if (snap?.realSiteId) {
      try {
        await firstValueFrom(this.listingSvc.remove(listingId));
      } catch (err) {
        console.error('[draft] real listing delete failed', err);
        this.toasts.error('Listing removed locally, but the server copy may still exist.');
      }
    }
    // Refresh the real ownership cache so the deleted listing drops out immediately.
    void this.hostListings.refreshNow();
    // Drop meta flags.
    this.meta.clear(listingId);
    // Async: drop snapshot from IDB. Don't block the caller, but surface a
    // toast if it fails so the host knows something's off.
    try {
      await deletePublishedSnapshot(listingId);
    } catch (err) {
      console.error('[draft] snapshot delete failed', err);
      this.toasts.error('Listing removed locally, but storage cleanup failed.');
    }
  }

  /**
   * Required-field check used by the publish gate. Deliberately does NOT require a
   * minimum photo count — the server already allows publishing with <3 photos and
   * starts a 30-day grace period from `publishDate` before disabling the listing
   * (see `canEnable()`/`enable()` in server/src/listing/listing.service.ts). The
   * review step surfaces that grace period as a separate non-blocking warning
   * instead of listing it here.
   *
   * Also mirrors the backend's account-profile gate — `testUserProfile()` in
   * server/src/listing/listing.service.ts requires the account holder's
   * firstName/lastName/phone, which the wizard itself never collects. Checking
   * it here (rather than only discovering it after the publish click fails)
   * lets the review step warn the host up front.
   *
   * The same reasoning covers the two site-level rules below. `enable()` on a
   * listing auto-enables its first site first (listing.service.ts:1551) and
   * aborts the whole publish if that site fails `canEnable()` — and a failure
   * there is expensive, because `createRealListing()` has already created the
   * listing and there is no rollback, so every rejected publish orphans another
   * listing. Anything this gate misses, a host pays for in orphans.
   *
   * Only rules whose values actually reach the server are checked. `site.name`
   * and `site.vehicles` are server-required but never sent by the publish path
   * (`fillSiteContent()` sends details, isolation and price only), so the server
   * must be defaulting them; gating on `draft.vehicleTypes` would block a host on
   * an answer that never leaves the browser. That gap is its own defect.
   */
  missingRequiredFields(draft: IDraftListing): string[] {
    const missing: string[] = [];
    if (!draft.primaryType) missing.push('primary property type');
    else if (draft.primaryType === 'custom' && (draft.customPrimaryLabel?.trim().length ?? 0) < 2) {
      missing.push('custom property-type label');
    }
    if (!draft.address?.city || !draft.address?.state) missing.push('address');
    if (typeof draft.lat !== 'number' || typeof draft.lng !== 'number') missing.push('location pin');
    if (draft.isLandowner === false && !isAddressStepValid(draft)) {
      missing.push('landowner details');
    }
    if ((draft.amenities?.length ?? 0) + (draft.customAmenities?.length ?? 0) === 0) {
      missing.push('amenities');
    }
    // Server: testAmenity() (site.service.ts:1382) — "Please select how RVs can
    // be parked." Both values ride along in the site's amenity list, so this is
    // a rule the client can genuinely pre-empt.
    if (!draft.amenities?.some((a) => a === 'pullThrough' || a === 'backIn')) {
      missing.push('how RVs park (pull-through or back-in)');
    }
    // Server: testPrivacy() (site.service.ts:1424) — "Please select the level of
    // isolation the site has." mapPrivacy() turns an empty `visibility` into four
    // false flags, which the server rejects every time. This is the rule that was
    // actually orphaning listings: stepValidities() requires visibility for phase 2
    // step 2, but this gate did not, so a draft that skipped that step passed here
    // and died at the server after the listing had already been created.
    if (!draft.visibility?.length) {
      missing.push('site visibility (how private the site is)');
    }
    // Server: testVehicles() (site.service.ts:1404) — "Please select what RVs are
    // allowed on the site." This gate was deliberately left out while
    // fillSiteContent() did not transmit the answer, because blocking a host on a
    // value the server never received would have stopped valid publishes for
    // nothing. F15 now sends it, so the rule belongs here alongside the others it
    // mirrors — and phase 1 step 5 already refuses to complete without it.
    if (draft.tentMode !== 'tents-only' && !draft.vehicleTypes?.length) {
      missing.push('which rig types you accept (or tents-only)');
    }
    if (!draft.title || draft.title.length < 8) missing.push('title');
    if (!draft.description || draft.description.length < 150) missing.push('description');
    // F7: a $10 floor used to block publishing here, on the belief that the fee
    // came out of the host's price and left them nothing. It does not — it is
    // added on top — so only a missing or non-positive price blocks now.
    if (typeof draft.nightlyPrice !== 'number' || draft.nightlyPrice <= 0) {
      missing.push('price');
    }
    if (!draft.cancellationTier) missing.push('cancellation policy');
    const profile = this.hostProfileFor(draft);
    if (!profile.firstName) missing.push('first name');
    if (!profile.lastName) missing.push('last name');
    if (!profile.phone) missing.push('phone number');
    return missing;
  }

  /**
   * The host's name/phone as the publish gate sees them: the account's value
   * first, falling back to what a signed-out host typed on the profile step
   * (`draft.hostProfile`). `publish()` copies the fallbacks onto the account.
   */
  private hostProfileFor(d: IDraftListing): { firstName: string; lastName: string; phone: string } {
    const user = this.auth.currentUser;
    const pick = (account?: string, draft?: string) => account?.trim() || draft?.trim() || '';
    return {
      firstName: pick(user?.firstName, d.hostProfile?.firstName),
      lastName: pick(user?.lastName, d.hostProfile?.lastName),
      phone: pick(user?.phone, d.hostProfile?.phone),
    };
  }

  private isHostProfileComplete(d: IDraftListing): boolean {
    const p = this.hostProfileFor(d);
    return !!p.firstName && !!p.lastName && !!p.phone;
  }

  /**
   * Copy draft-held name/phone onto the account for any field the account
   * lacks — the server's `testUserProfile()` reads the account, not the draft.
   * Never overwrites a value the account already has. A draft-held photo (picked
   * while signed out) is uploaded as the account photo; a failed upload is only
   * a toast, since the server doesn't require a photo to publish.
   */
  private async syncHostProfileToAccount(draft: IDraftListing): Promise<void> {
    if (draft.profilePhoto?.startsWith('data:')) {
      const blob = await (await fetch(draft.profilePhoto)).blob();
      const uploaded = await this.auth.uploadProfilePhoto(new File([blob], 'profile.jpg', { type: blob.type }));
      if (!uploaded) this.toasts.error('Could not upload your profile photo — add it from your account page.');
    }
    const user = this.auth.currentUser;
    const patch: { firstName?: string; lastName?: string; phone?: string } = {};
    if (!user?.firstName?.trim() && draft.hostProfile?.firstName?.trim()) patch.firstName = draft.hostProfile.firstName;
    if (!user?.lastName?.trim() && draft.hostProfile?.lastName?.trim()) patch.lastName = draft.hostProfile.lastName;
    if (!user?.phone?.trim() && draft.hostProfile?.phone?.trim()) patch.phone = draft.hostProfile.phone;
    if (Object.keys(patch).length === 0) return;
    const updated = await this.auth.updateProfile(patch);
    if (!updated) throw new Error('Could not save your name and phone number to your account.');
  }

  // ─────────────────────── internals ───────────────────────

  private write(draft: IDraftListing): void {
    if (!isPlatformBrowser(this.platformId)) return;
    writeActiveDraft(draft).catch((err) => {
      console.error('[draft] write failed', err);
      this.toasts.error('Could not save your listing progress — storage may be full.');
    });
  }

  private newId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
    return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  /**
   * Build an `IPrivateListing` from the draft, under the real backend id assigned
   * by `createRealListing()`. Picks a `category` from the first descriptor where
   * possible (with a generic 'offgrid' fallback).
   */
  private draftToListing(draft: IDraftListing, id: number): IPrivateListing {
    const category = this.inferCategory(draft);
    const location = draft.address ? `${draft.address.city}, ${draft.address.state}` : 'Location TBD';
    return {
      id,
      title: draft.title ?? `Listing ${id}`,
      location,
      coordinates: { lat: draft.lat ?? 0, lon: draft.lng ?? 0 },
      price: draft.nightlyPrice ?? 0,
      rating: 0, // no reviews yet — listing renders without a star chip
      reviewCount: 0,
      category,
      landTypes: landTypesForCategory(category),
      amenities: draft.amenities ?? [],
      amenityDetails: amenityDetailsFrom(draftToRealSiteDetails(draft).amenities),
      image: draft.photos?.[0] ?? 'assets/images/host_opportunity.webp',
      reservationProcess: draft.bookability === 'instant' ? 'instant' : 'standard',
    };
  }

  /**
   * Map the host's selections to a platform Category bucket. Prefers the
   * single-select primaryType (new model); falls back to first descriptor for
   * legacy drafts that predate the primary/secondary split.
   */
  private inferCategory(draft: IDraftListing): Category {
    if (draft.primaryType) {
      return PRIMARY_PROPERTY_TYPE_META[draft.primaryType].category;
    }
    // Legacy fallback: first descriptor (kept for drafts created before the
    // primary/secondary split).
    const first = draft.descriptors?.[0] as string | undefined;
    switch (first) {
      case 'winery':
        return 'vineyard';
      case 'brewery':
        return 'brewery';
      case 'distillery':
        return 'brewery';
      case 'farmland':
      case 'orchard':
        return 'farm';
      case 'urban':
      case 'suburban':
        return 'attraction';
      default:
        return 'offgrid';
    }
  }

  /** "Dustin R." from the current user, or "New host" if unauthenticated. */
  private hostNameFromUser(user: { firstName?: string; lastName?: string } | null | undefined): string {
    const first = user?.firstName?.trim() ?? '';
    const last = user?.lastName?.trim() ?? '';
    if (!first && !last) return 'New host';
    if (!last) return first;
    return `${first} ${last.charAt(0).toUpperCase()}.`;
  }
  /** "DR" from "Dustin Reed". */
  private hostInitialsFromUser(user: { firstName?: string; lastName?: string } | null | undefined): string {
    const f = (user?.firstName?.trim() ?? '').charAt(0).toUpperCase();
    const l = (user?.lastName?.trim() ?? '').charAt(0).toUpperCase();
    return `${f}${l}` || 'NH';
  }

  /** Write the per-listing snapshot so /listing?id=N can hydrate the host's real data.
   * Fires a toast on storage failure (quota / IDB unavailable) instead of silently
   * dropping the write — the cache update has already happened in memory. */
  private savePublishedSnapshot(listingId: number, snapshot: IPublishedSnapshot): void {
    writePublishedSnapshot(listingId, snapshot).catch((err) => {
      console.error('[draft] snapshot write failed', err);
      this.toasts.error('Could not save listing changes — storage may be full.');
    });
  }
}
